import React from 'react';

import { Spinner } from '@oysterjs/ui/spinner';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { MultiCombobox } from '@oysterjs/ui/multicombobox';

import { useTemplateLimit } from '~/components/presets/template-limit';

export type TemplatePickerOption = { id: string; title: string };

/**
 * Reusable dialog for the three library pickers (custom forms, Word
 * templates, HTML document templates). All three need the same UX — a
 * searchable multi-select combobox bounded by `useTemplateLimit`, the same
 * cap as the ACORD pickers', with a per-kind heading/copy — so we hoist the
 * shell here and let the caller pass kind-specific text and the options array.
 *
 * Options are always sorted alphabetically before being handed to the
 * combobox. The combobox itself handles search/filter and shows selected
 * items as removable chips.
 */
export const CaraInputTemplatePickerDialog = (props: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: React.ReactNode;
  fieldLabel: string;
  placeholder: string;
  options: TemplatePickerOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** When set and `options` is empty, show this copy instead of an empty combobox. */
  emptyText?: string;
  loading?: boolean;
  loadingMessage?: string;
}) => {
  const limit = useTemplateLimit();
  const sortedOptions = React.useMemo(
    () =>
      [...props.options]
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((opt) => ({ value: opt.id, label: opt.title })),
    [props.options]
  );

  return (
    <Dialog open={props.open} onClose={props.onClose} size="md">
      <DialogTitle>{props.title}</DialogTitle>
      <DialogDescription>{props.description}</DialogDescription>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>{props.fieldLabel}</Label>
              {props.loading ? (
                <div className="flex items-center gap-2 py-2 text-sm text-neutral-500 dark:text-neutral-400">
                  <Spinner size="size-4" color="neutral" />
                  {props.loadingMessage ?? 'Loading\u2026'}
                </div>
              ) : props.options.length === 0 && props.emptyText ? (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 m-0 py-2">
                  {props.emptyText}
                </p>
              ) : (
                <MultiCombobox
                  value={props.selectedIds}
                  options={sortedOptions}
                  placeholder={props.placeholder}
                  maxSelections={limit.count}
                  onChange={props.onChange}
                />
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button type="button" color="sky" onClick={props.onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
